import { queryOptions, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { orgQueryKeys } from "./orgQueryKeys";

/** How long a fetched org record is served from cache. 5 minutes, chosen arbitrarily. */
export const ORG_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * The query that fetches an org's record, shared by {@link useOrg} and callers
 * that read several orgs at once so they all land on the same cache entries.
 */
export function useOrgQueryOptions() {
  // Read in the query function, not here: harnesses that do not stub
  // `orgService` throw on access, and the event-model hooks call this from
  // pages that otherwise never touch the org record.
  const services = useDomainServices();

  return (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.org(orgId),
      queryFn: ({ signal }) => {
        if (orgId === undefined) {
          return Promise.reject(new Error("Invalid orgId"));
        }

        return services.orgService.getOrg(orgId, {
          abortSignal: signal,
        });
      },
      enabled: orgId !== undefined,
      staleTime: ORG_STALE_TIME_MS,
    });
}

export function useOrg(orgId: string | undefined) {
  const orgQueryOptions = useOrgQueryOptions();
  return useQuery(orgQueryOptions(orgId));
}
